import { z } from 'zod';
import { emailSchema, phoneSchema } from './schemas.ts';

/** GST state / UT codes. */
export const GST_STATES: { code: string; name: string }[] = [
  { code: '01', name: 'Jammu and Kashmir' }, { code: '02', name: 'Himachal Pradesh' }, { code: '03', name: 'Punjab' },
  { code: '04', name: 'Chandigarh' }, { code: '05', name: 'Uttarakhand' }, { code: '06', name: 'Haryana' }, { code: '07', name: 'Delhi' },
  { code: '08', name: 'Rajasthan' }, { code: '09', name: 'Uttar Pradesh' }, { code: '10', name: 'Bihar' }, { code: '11', name: 'Sikkim' },
  { code: '12', name: 'Arunachal Pradesh' }, { code: '13', name: 'Nagaland' }, { code: '14', name: 'Manipur' }, { code: '15', name: 'Mizoram' },
  { code: '16', name: 'Tripura' }, { code: '17', name: 'Meghalaya' }, { code: '18', name: 'Assam' }, { code: '19', name: 'West Bengal' },
  { code: '20', name: 'Jharkhand' }, { code: '21', name: 'Odisha' }, { code: '22', name: 'Chhattisgarh' }, { code: '23', name: 'Madhya Pradesh' },
  { code: '24', name: 'Gujarat' }, { code: '26', name: 'Dadra and Nagar Haveli and Daman and Diu' }, { code: '27', name: 'Maharashtra' },
  { code: '29', name: 'Karnataka' }, { code: '30', name: 'Goa' }, { code: '31', name: 'Lakshadweep' }, { code: '32', name: 'Kerala' },
  { code: '33', name: 'Tamil Nadu' }, { code: '34', name: 'Puducherry' }, { code: '35', name: 'Andaman and Nicobar Islands' },
  { code: '36', name: 'Telangana' }, { code: '37', name: 'Andhra Pradesh' }, { code: '38', name: 'Ladakh' }, { code: '97', name: 'Other Territory' },
];
export const stateName = (code: string | null | undefined) => GST_STATES.find((s) => s.code === code)?.name ?? '';
/** The first two digits of a GSTIN are the state code. */
export const stateFromGstin = (gstin: string | null | undefined) => (gstin && /^\d{2}/.test(gstin) ? gstin.slice(0, 2) : null);

export const GST_RATES = [0, 5, 12, 18, 28] as const;
export const UNITS = ['Nos', 'Pcs', 'Kg', 'Gm', 'Ltr', 'Mtr', 'Sqft', 'Box', 'Set', 'Pair', 'Hrs', 'Day', 'Month', 'Job', 'Service'] as const;

const money = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export interface ItemInput { qty: number; rate: number; discountPct?: number; gstRate: number }
export interface ItemAmounts { taxable: number; cgst: number; sgst: number; igst: number; total: number }
export interface DocTotals {
  subtotal: number; discountTotal: number; taxableTotal: number; cgstTotal: number; sgstTotal: number; igstTotal: number; roundOff: number; grandTotal: number;
}

/**
 * GST maths used by both the screen (live totals) and the server (final numbers).
 * Same state → CGST + SGST (half each). Different state → IGST. Grand total rounded to the rupee.
 */
export function computeDocument(items: ItemInput[], interstate: boolean): { lines: ItemAmounts[]; totals: DocTotals } {
  let subtotal = 0, discountTotal = 0, taxableTotal = 0, cgstTotal = 0, sgstTotal = 0, igstTotal = 0;
  const lines = items.map((it) => {
    const gross = money(it.qty * it.rate);
    const discount = money(gross * ((it.discountPct ?? 0) / 100));
    const taxable = money(gross - discount);
    const tax = money(taxable * (it.gstRate / 100));
    const cgst = interstate ? 0 : money(tax / 2);
    const sgst = interstate ? 0 : money(tax - cgst);
    const igst = interstate ? tax : 0;
    subtotal += gross; discountTotal += discount; taxableTotal += taxable; cgstTotal += cgst; sgstTotal += sgst; igstTotal += igst;
    return { taxable, cgst, sgst, igst, total: money(taxable + cgst + sgst + igst) };
  });
  const exact = money(taxableTotal + cgstTotal + sgstTotal + igstTotal);
  const grandTotal = Math.round(exact);
  return {
    lines,
    totals: {
      subtotal: money(subtotal), discountTotal: money(discountTotal), taxableTotal: money(taxableTotal),
      cgstTotal: money(cgstTotal), sgstTotal: money(sgstTotal), igstTotal: money(igstTotal), roundOff: money(grandTotal - exact), grandTotal,
    },
  };
}

const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
function twoDigits(n: number) { return n < 20 ? ONES[n]! : `${TENS[Math.floor(n / 10)]}${n % 10 ? ` ${ONES[n % 10]}` : ''}`; }
function threeDigits(n: number) { const h = Math.floor(n / 100); const r = n % 100; return [h ? `${ONES[h]} Hundred` : '', r ? twoDigits(r) : ''].filter(Boolean).join(' '); }

/** 120500.5 → "Rupees One Lakh Twenty Thousand Five Hundred and Fifty Paise Only" (Indian numbering). */
export function amountInWords(amount: number): string {
  const rupees = Math.floor(Math.abs(amount));
  const paise = Math.round((Math.abs(amount) - rupees) * 100);
  const parts: string[] = [];
  const crore = Math.floor(rupees / 10_000_000);
  const lakh = Math.floor((rupees % 10_000_000) / 100_000);
  const thousand = Math.floor((rupees % 100_000) / 1000);
  const rest = rupees % 1000;
  if (crore) parts.push(`${crore >= 100 ? threeDigits(crore) : twoDigits(crore)} Crore`);
  if (lakh) parts.push(`${twoDigits(lakh)} Lakh`);
  if (thousand) parts.push(`${twoDigits(thousand)} Thousand`);
  if (rest) parts.push(threeDigits(rest));
  const words = parts.join(' ') || 'Zero';
  return `Rupees ${words}${paise ? ` and ${twoDigits(paise)} Paise` : ''} Only`;
}

/** Indian financial year for a date, e.g. 2026-10-05 → "2026-27". */
export function financialYear(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${String((y + 1) % 100).padStart(2, '0')}`;
}

/* Schemas -------------------------------------------------------------------------------------- */

const optText = (max: number) => z.string().trim().max(max).optional().or(z.literal('').transform(() => undefined));
const gstin = z.string().trim().toUpperCase().regex(/^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/, { error: 'GSTIN should look like 36ABCDE1234F1Z5.' })
  .optional().or(z.literal('').transform(() => undefined));
const stateCode = z.string().regex(/^\d{2}$/, { error: 'Choose a state.' });

export const productSchema = z.object({
  name: z.string().trim().min(1, { error: 'Enter a name.' }).max(200),
  description: optText(2000),
  sku: optText(60),
  hsn: z.string().trim().regex(/^\d{4,8}$/, { error: 'HSN/SAC is 4 to 8 digits.' }).optional().or(z.literal('').transform(() => undefined)),
  unit: z.string().trim().min(1).max(20).default('Nos'),
  price: z.coerce.number().min(0, { error: 'Price can’t be negative.' }).max(1e10),
  gstRate: z.coerce.number().refine((v) => [0, 0.25, 3, 5, 12, 18, 28].includes(v), { error: 'Choose a GST rate.' }),
  priceIncludesTax: z.boolean().default(false),
  isActive: z.boolean().default(true),
});

export const customerSchema = z.object({
  name: z.string().trim().min(1, { error: 'Enter the customer or business name.' }).max(200),
  contactPerson: optText(120),
  phone: phoneSchema.optional().or(z.literal('').transform(() => undefined)),
  email: emailSchema.optional().or(z.literal('').transform(() => undefined)),
  gstin,
  stateCode: stateCode.optional().or(z.literal('').transform(() => undefined)),
  billingAddress: optText(1000),
  shippingAddress: optText(1000),
  leadId: z.uuid().nullable().optional(),
});

export const documentItemSchema = z.object({
  productId: z.uuid().nullable().optional(),
  description: z.string().trim().min(1, { error: 'Describe the item.' }).max(1000),
  hsn: optText(8),
  qty: z.coerce.number().positive({ error: 'Quantity must be more than 0.' }).max(1e7),
  unit: z.string().trim().min(1).max(20).default('Nos'),
  rate: z.coerce.number().min(0).max(1e10),
  discountPct: z.coerce.number().min(0).max(100).default(0),
  gstRate: z.coerce.number().refine((v) => [0, 0.25, 3, 5, 12, 18, 28].includes(v), { error: 'Choose a GST rate.' }),
});

export const documentSchema = z.object({
  docType: z.enum(['quotation', 'proforma', 'invoice']),
  customerId: z.uuid({ error: 'Choose a customer.' }),
  leadId: z.uuid().nullable().optional(),
  issueDate: z.iso.date(),
  dueDate: z.iso.date().nullable().optional(),
  placeOfSupply: stateCode.optional(),
  items: z.array(documentItemSchema).min(1, { error: 'Add at least one item.' }).max(200),
  notes: optText(4000),
  terms: optText(4000),
});

export const paymentSchema = z.object({
  amount: z.coerce.number().positive({ error: 'Enter the amount received.' }).max(1e10),
  paidOn: z.iso.date(),
  mode: z.enum(['cash', 'upi', 'bank', 'card', 'cheque', 'other']),
  reference: optText(120),
  note: optText(500),
});

export const billingSettingsSchema = z.object({
  stateCode: stateCode,
  quotationPrefix: z.string().trim().max(12).default('QT'),
  proformaPrefix: z.string().trim().max(12).default('PI'),
  invoicePrefix: z.string().trim().max(12).default('INV'),
  quotationValidityDays: z.coerce.number().int().min(1).max(365).default(15),
  invoiceDueDays: z.coerce.number().int().min(0).max(365).default(7),
  defaultTerms: optText(4000),
  bankDetails: optText(1000),
  upiId: z.string().trim().regex(/^[\w.\-]{2,256}@[a-zA-Z]{2,64}$/, { error: 'UPI ID looks like name@bank.' }).optional().or(z.literal('').transform(() => undefined)),
});
export type BillingSettings = z.infer<typeof billingSettingsSchema>;

export const targetSchema = z.object({
  userId: z.uuid().nullable(),
  month: z.string().regex(/^\d{4}-\d{2}$/),
  revenueTarget: z.coerce.number().min(0).max(1e12),
  wonTarget: z.coerce.number().int().min(0).max(1e6),
});

export const DOC_LABELS = { quotation: 'Quotation', proforma: 'Proforma invoice', invoice: 'Tax invoice' } as const;
